import { getColumnId, getColumnMeta } from './columnLayout/getColumnId';

/**
 * Converts a sort string (e.g., "name,desc" or "createdAt,asc") to the table's sorting state
 * @param sortString - Sort string in format "columnId,direction"
 * @param columns - Table columns array
 * @returns Array with single sort rule or undefined if invalid
 */
export const parseSortString = (
    sortString: string,
    columns: object[]
): Array<{ id: string; desc: boolean }> | undefined => {
    if (!sortString) return undefined;

    const [sortAccessor, direction] = sortString.split(',');
    if (!sortAccessor) return undefined;

    // Find column by id or sortAccessor
    const column = columns.find(
        (col) => getColumnId(col) === sortAccessor || getColumnMeta(col).sortAccessor === sortAccessor
    );

    if (!column) return undefined;

    return [{ id: getColumnId(column) as string, desc: direction === 'desc' }];
};

/**
 * Converts the table's sorting state to a sort string (e.g., "name,desc" or "paidOnDate,desc,nullsLast")
 * @param sortBy - Sorting state array
 * @param columns - Table columns array
 * @returns Sort string in format "columnId,direction[,nullHandling]" or empty string
 */
export const convertSortToString = (sortBy: Array<{ id: string; desc?: boolean }>, columns: object[]): string => {
    if (!sortBy || sortBy.length === 0) return '';

    const columnSortingRule = sortBy[0];
    const sortedColumn = columns.find((col) => getColumnId(col) === columnSortingRule.id);

    if (!sortedColumn) return '';

    const { sortAccessor, nullHandling } = getColumnMeta(sortedColumn);
    const accessor = sortAccessor || columnSortingRule.id;
    const direction = columnSortingRule.desc ? 'desc' : 'asc';

    return nullHandling ? `${accessor},${direction},${nullHandling}` : `${accessor},${direction}`;
};

/**
 * Cycles the column sort state on header click for plain tables that don't go through the
 * table engine at all. Same encoding as `parseSortString` / `convertSortToString`
 * (`${columnId},asc` | `${columnId},desc` | empty), so a feature can mix engine-driven tiers
 * and hand-rolled tiers without divergent sort semantics. Cycle: unsorted → desc → asc →
 * unsorted (numeric-friendly default); `ascFirst` flips it for text columns, where the first
 * click is expected to give A→Z.
 */
export const cycleSort = (currentSort: string, columnId: string, ascFirst = false): string => {
    const [activeId, activeDir] = currentSort.split(',');
    const firstDir = ascFirst ? 'asc' : 'desc';
    if (activeId !== columnId) return `${columnId},${firstDir}`;
    if (activeDir === firstDir) return `${columnId},${ascFirst ? 'desc' : 'asc'}`;
    return '';
};

/**
 * Generic numeric sort helper for the nested tiers under a list-table row expansion.
 * Rows whose accessor returns `null` or `undefined` drop to the end regardless of
 * direction so empty cells never bubble up to the top of the table.
 */
export const sortByNumericAccessor = <T>(
    items: T[],
    accessor: (item: T) => number | null | undefined,
    desc: boolean
): T[] =>
    items.toSorted((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        const aMissing = av == null;
        const bMissing = bv == null;
        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;
        return desc ? (bv as number) - (av as number) : (av as number) - (bv as number);
    });

/**
 * Generic value-type-agnostic sort for nested-tier columns. Accepts numeric or string
 * values (or null/undefined, which always drop to the end). Strings use a locale-aware
 * collator with case-folding (sensitivity 'base'), so name sort matches the alphabetical
 * ordering users expect across locales. Dates serialised as ISO 8601 ('yyyy-MM-dd' or
 * 'yyyy-MM-ddTHH:mm:ssZ') sort correctly under string comparison without extra parsing.
 */
const accessorCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

export const sortByAccessor = <T>(
    items: T[],
    accessor: (item: T) => number | string | null | undefined,
    desc: boolean
): T[] => {
    return items.toSorted((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        const aMissing = av == null;
        const bMissing = bv == null;
        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;
        const cmp =
            typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : accessorCollator.compare(String(av), String(bv));
        return desc ? -cmp : cmp;
    });
};
